// Invoicing page: every invoice not in QuickBooks yet, ticked, one button to
// send them (Stephanie 2026-11-03: "when i go into the invoice tab, i expect to
// easily and quickly export any invoice that hasn't been exported").
// The same queue and send as Finance › QuickBooks (GET /export/queue,
// POST /export) — the server decides what's ready and does every sum; a held
// invoice can't be ticked and says why. If sending is switched off or
// QuickBooks isn't connected, the button is off and the reason shows.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { errText, formatDate, type ExportResponse, type QboStatus, type Queue } from "@/pages/qbo/types";

export function QboInvoiceExportCard() {
  const qc = useQueryClient();
  const statusQ = useQuery({ queryKey: ["qbo", "status"], queryFn: () => api<QboStatus>("/api/finance/qbo/status") });
  const queueQ = useQuery({
    queryKey: ["qbo", "queue", "invoices"],
    queryFn: () => api<Queue>("/api/finance/qbo/export/queue?entity=invoices"),
  });
  const rows = useMemo(() => queueQ.data?.rows ?? [], [queueQ.data]);
  const readyIds = useMemo(() => rows.filter((r) => (r.state ?? "not_sent") === "not_sent").map((r) => r.id), [rows]);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const selected = picked ?? new Set(readyIds);
  const selectedIds = rows.filter((r) => r.sendable !== false && selected.has(r.id)).map((r) => r.id);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<ExportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api<ExportResponse>("/api/finance/qbo/export", { method: "POST", body: JSON.stringify({ entity: "invoices", ids: selectedIds }) }),
    onSuccess: (res) => {
      setResult(res);
      setError(null);
      setConfirming(false);
      setPicked(null);
      void qc.invalidateQueries({ queryKey: ["qbo"] });
      void qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e) => {
      setConfirming(false);
      setError(errText(e, "Nothing was sent to QuickBooks. Try again in a minute."));
    },
  });

  if (queueQ.isLoading) return null;
  if (queueQ.isError) {
    return (
      <section className="mb-7 rounded-md border border-rule bg-white p-5 text-sm text-rust-600">
        {errText(queueQ.error, "Couldn't load the invoices waiting for QuickBooks — refresh to try again.")}
      </section>
    );
  }
  if (rows.length === 0 && !result) return null;

  const st = statusQ.data;
  const blocked = !st
    ? null
    : !st.configured || !st.connected
      ? "QuickBooks isn't connected, so nothing can be sent. Ask Jake to connect it."
      : st.exportAllowed !== true
        ? (st.exportLockedReason ?? "Sending to QuickBooks is off for now.")
        : null;
  const canSend = !!st && blocked === null && selectedIds.length > 0 && !send.isPending;
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const byId = new Map((result?.results ?? []).map((r) => [r.id, r]));

  return (
    <section className="mb-7 rounded-md border border-rule bg-white" aria-labelledby="qbo-invoices-title">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-5 py-4">
        <div>
          <h2 id="qbo-invoices-title" className="text-base font-semibold text-ink">
            Not in QuickBooks yet{rows.length > 0 ? ` (${rows.length})` : ""}
          </h2>
          <p className="mt-0.5 text-xs text-muted">Ticked invoices go to QuickBooks in one click.</p>
        </div>
        {confirming ? (
          <span className="flex items-center gap-2 text-sm text-ink">
            Send {selectedIds.length} invoice{selectedIds.length === 1 ? "" : "s"} to QuickBooks?
            <button type="button" onClick={() => send.mutate()} disabled={!canSend} className="rounded-md bg-ink px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
              {send.isPending ? "Sending…" : "Yes, send"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="px-1 text-sm text-ink">Cancel</button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirming(true)} disabled={!canSend} className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            Send {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}to QuickBooks
          </button>
        )}
      </div>
      {blocked && <p className="border-b border-rule bg-cream-50 px-5 py-2.5 text-sm text-ink">{blocked}</p>}
      {error && <p className="border-b border-rule px-5 py-2.5 text-sm text-rust-600">{error}</p>}
      {result && (
        <p className="border-b border-rule px-5 py-2.5 text-sm text-ink">
          {result.sent} sent{result.failed ? ` · ${result.failed} didn't go — see Finance › QuickBooks › Needs attention` : ""}
          {result.held ? ` · ${result.held} waiting` : ""}.
        </p>
      )}
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <tbody className="divide-y divide-rule">
            {rows.map((r) => {
              const res = byId.get(r.id);
              const sendable = r.sendable !== false;
              return (
                <tr key={r.id}>
                  <td className="w-10 py-2.5 pl-5">
                    <input
                      type="checkbox"
                      aria-label={`Send invoice ${r.docNumber}`}
                      checked={sendable && selected.has(r.id)}
                      disabled={!sendable}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                  <td className="py-2.5 font-medium text-ink">#{r.docNumber}</td>
                  <td className="py-2.5 text-ink">{r.name}</td>
                  <td className="py-2.5 text-muted">{formatDate(r.date)}</td>
                  <td className="py-2.5 text-xs text-muted">
                    {res ? (res.ok && res.stamped !== false ? "Sent" : res.error ?? "Didn't go") : sendable ? (r.state === "failed" ? `Didn't go last time${r.lastErrorHeadline ? ` — ${r.lastErrorHeadline}` : ""}` : "") : (r.stateLabel ?? r.detail ?? "Waiting")}
                  </td>
                  <td className="py-2.5 pr-5 text-right font-medium tabular-nums text-ink">{r.amount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="px-5 py-2.5 text-xs text-muted">
        Bills, payments and the full history are on <Link to="/finance/quickbooks?entity=invoices" className="underline">Finance › QuickBooks</Link>.
      </p>
    </section>
  );
}
